import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import DetailActions from "@/components/feature/DetailActions";
import EmptyState from "@/components/base/EmptyState";
import { movies, getMovieById } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  const relatedNews = useMemo(() => {
    if (!movie) return [];
    return newsList.filter((n) => n.relatedMovieId === movie.id).slice(0, 4);
  }, [movie]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .sort((a, b) => {
        const aScore = a.tags.filter((t) => movie.tags.includes(t)).length;
        const bScore = b.tags.filter((t) => movie.tags.includes(t)).length;
        return bScore - aScore;
      })
      .slice(0, 3);
  }, [movie]);

  useDocumentMeta(
    movie ? `${movie.title} | 小黄人电影` : "电影详情 | 小黄人电影",
    movie ? movie.synopsis.slice(0, 100) : undefined
  );

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-film-line"
            title="电影信息不存在"
            desc="你访问的影片可能已经被移除，或者链接有误。回到片库看看其他作品吧。"
            actionLabel="返回片库"
            actionTo="/category"
            actionIcon="ri-arrow-left-line"
          />
        </div>
      </SiteLayout>
    );
  }

  const infoItems = [
    { label: "导演", value: movie.director, icon: "ri-movie-line" },
    { label: "上映年份", value: String(movie.year), icon: "ri-calendar-line" },
    { label: "影片类型", value: movie.type, icon: "ri-price-tag-3-line" },
    { label: "片长", value: movie.duration, icon: "ri-time-line" },
    { label: "语言", value: movie.language, icon: "ri-translate-2" },
    { label: "上映日期", value: movie.releaseDate, icon: "ri-calendar-check-line" },
  ];

  return (
    <SiteLayout>
      {/* hero */}
      <div className="relative w-full overflow-hidden">
        <img
          src={movie.wide}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} 小黄人电影 剧照`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40"></div>
        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            light
            items={[
              { label: "首页", to: "/" },
              { label: "电影分类", to: "/category" },
              { label: movie.title },
            ]}
          />
          <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end md:gap-8">
            <div className="w-40 shrink-0 overflow-hidden rounded-2xl border-4 border-background-50/20 md:w-52">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 小黄人电影 海报`}
                className="aspect-[2/3] h-full w-full object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-foreground-950">
                <i className="ri-price-tag-3-line"></i>
                {movie.type}
              </span>
              <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-1.5 text-sm text-background-200">
                {movie.originalTitle} · {movie.year}
              </p>
              <p className="mt-3 max-w-2xl text-sm italic leading-relaxed text-background-100/90">
                「{movie.tagline}」
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 rounded-full bg-accent-500 px-3.5 py-1.5 text-sm font-bold text-background-50">
                  <i className="ri-star-fill"></i>
                  {movie.rating}
                  <span className="text-xs font-normal opacity-80">/ 10</span>
                </span>
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-50"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          {/* main */}
          <div className="min-w-0 space-y-10">
            <section>
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-book-open-line"></i>
                </span>
                剧情介绍
              </h2>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700">{movie.story}</p>
            </section>

            <section>
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-group-line"></i>
                </span>
                角色介绍
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {movie.characters.map((character) => (
                  <div
                    key={character.name}
                    className="flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-bold text-primary-800">
                      {character.name.slice(0, 1)}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground-950">
                          {character.name}
                        </h3>
                        <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] text-foreground-500">
                          {character.role}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                        {character.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-newspaper-line"></i>
                </span>
                相关新闻
              </h2>
              {relatedNews.length === 0 ? (
                <p className="mt-4 rounded-2xl border border-dashed border-background-300 bg-background-50 px-5 py-6 text-sm text-foreground-500">
                  暂时没有与该片相关的资讯，你可以前往
                  <Link to="/news" className="mx-1 text-primary-700 underline">
                    资讯频道
                  </Link>
                  查看更多内容。
                </p>
              ) : (
                <ul className="mt-4 space-y-2">
                  {relatedNews.map((n) => (
                    <li key={n.id}>
                      <Link
                        to={`/news/${n.id}`}
                        className="group flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3.5 transition hover:border-primary-300"
                      >
                        <i className="ri-article-line text-lg text-primary-600"></i>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                            {n.title}
                          </span>
                          <span className="mt-0.5 block text-xs text-foreground-500">
                            {n.date} · {n.category}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* sidebar */}
          <aside className="space-y-6">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-bold text-foreground-950">影片信息</h3>
              <ul className="mt-4 space-y-3">
                {infoItems.map((item) => (
                  <li key={item.label} className="flex items-start gap-3 text-sm">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    <span className="flex-1">
                      <span className="block text-xs text-foreground-500">{item.label}</span>
                      <span className="mt-0.5 block text-foreground-900">{item.value}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-bold text-foreground-950">收藏与分享</h3>
              <div className="mt-4">
                <DetailActions
                  type="movie"
                  id={movie.id}
                  title={movie.title}
                  path={`/detail/${movie.id}`}
                />
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-movie-2-line"></i>
            </span>
            相关影片推荐
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
            {relatedMovies.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}